import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { KeyRound, Shield, ShieldOff, Trash2, Users as UsersIcon, RefreshCw, UserPlus, Boxes } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Password reset dialog
  const [pwUser, setPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Role dialog
  const [roleUser, setRoleUser] = useState(null);
  const [newRole, setNewRole] = useState('user');

  // App access dialog
  const [appsUser, setAppsUser] = useState(null);
  const [apps, setApps] = useState({ budgets: false, sales: false, price_master: false, people: false, assets: false, tickets: false });

  // Price Master role dialog
  const [pmRoleUser, setPmRoleUser] = useState(null);
  const [pmRole, setPmRole] = useState('');

  // Create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'user' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  const openPwDialog = (u) => {
    setPwUser(u);
    setNewPassword('');
  };

  const submitPasswordReset = async () => {
    if (!pwUser) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/users/${pwUser.id}/password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${pwUser.email}`);
      setPwUser(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openRoleDialog = (u) => {
    setRoleUser(u);
    setNewRole(u.role || 'user');
  };

  const submitRoleChange = async () => {
    if (!roleUser) return;
    try {
      await axios.put(
        `${API}/admin/users/${roleUser.id}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success(`${roleUser.email} is now ${newRole}`);
      setRoleUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { withCredentials: true });
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openAppsDialog = (u) => {
    setAppsUser(u);
    const cur = u.app_access || [];
    setApps({ budgets: cur.includes('budgets'), sales: cur.includes('sales'), price_master: cur.includes('price_master'), people: cur.includes('people'), assets: cur.includes('assets'), tickets: cur.includes('tickets') });
  };

  const openPmRoleDialog = (u) => {
    setPmRoleUser(u);
    setPmRole(u.pm_role || '');
  };

  const submitPmRole = async () => {
    if (!pmRoleUser) return;
    try {
      await axios.put(
        `${API}/admin/users/${pmRoleUser.id}/pm-role`,
        { pm_role: pmRole || null },
        { withCredentials: true }
      );
      toast.success(`PM role updated for ${pmRoleUser.email}`);
      setPmRoleUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PM role');
    }
  };
  const submitAppAccess = async () => {
    if (!appsUser) return;
    const selected = Object.entries(apps).filter(([, v]) => v).map(([k]) => k);
    try {
      await axios.put(`${API}/admin/users/${appsUser.id}/app-access`, { apps: selected }, { withCredentials: true });
      toast.success(`Access updated for ${appsUser.email}`);
      setAppsUser(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openCreate = () => {
    setNewUser({ email: '', name: '', password: '', role: 'user' });
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!newUser.email.trim() || !newUser.name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/users`,
        {
          email: newUser.email.trim(),
          name: newUser.name.trim(),
          password: newUser.password,
          role: newUser.role,
        },
        { withCredentials: true }
      );
      toast.success(`User ${newUser.email.trim()} created`);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E5DF', borderRadius: '8px' }}
      className="p-6"
      data-testid="admin-users-panel"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <UsersIcon size={18} style={{ color: '#1E3B29' }} />
          <h2 className="text-lg font-medium" style={{ fontFamily: 'Manrope', color: '#1A1A1A' }}>
            Registered Users <span className="text-sm text-gray-500 font-normal">({filtered.length})</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search email, name, role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-64"
            data-testid="admin-users-search"
          />
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-users-refresh">
            <RefreshCw size={14} className="mr-1" />Refresh
          </Button>
          <Button
            size="sm"
            onClick={openCreate}
            data-testid="admin-create-user-btn"
            style={{ backgroundColor: '#1E3B29', color: '#FFFFFF' }}
          >
            <UserPlus size={14} className="mr-1" />Create User
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr style={{ backgroundColor: '#F7F5F0', color: '#5C5C5C' }} className="text-left">
              <th className="p-3 font-semibold">Email</th>
              <th className="p-3 font-semibold">Name</th>
              <th className="p-3 font-semibold">Role</th>
              <th className="p-3 font-semibold">Registered</th>
              <th className="p-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="p-6 text-center text-gray-500">Loading users…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-gray-500">No users match your search.</td></tr>
            )}
            {!loading && filtered.map((u) => {
              const isSelf = u.id === currentUser?.id;
              const isAdmin = u.role === 'admin';
              return (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-t"
                  style={{ borderColor: '#E8E5DF' }}
                >
                  <td className="p-3 font-medium" style={{ color: '#1A1A1A' }}>{u.email}</td>
                  <td className="p-3" style={{ color: '#1A1A1A' }}>{u.name}</td>
                  <td className="p-3">
                    <span
                      className="text-xs px-2 py-1 rounded font-semibold"
                      style={{
                        backgroundColor: isAdmin ? '#1E3B2915' : '#C77D3815',
                        color: isAdmin ? '#1E3B29' : '#C77D38',
                      }}
                    >
                      {isAdmin ? 'Admin' : 'User'}
                    </span>
                    {isSelf && (
                      <span className="ml-2 text-[10px] text-gray-500 uppercase tracking-wider">(you)</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-600 text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '—'}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPwDialog(u)}
                        data-testid={`reset-password-${u.id}`}
                      >
                        <KeyRound size={14} className="mr-1" />Reset Password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isSelf}
                        onClick={() => openRoleDialog(u)}
                        data-testid={`change-role-${u.id}`}
                        title={isSelf ? "You can't change your own role" : ''}
                      >
                        {isAdmin ? <ShieldOff size={14} className="mr-1" /> : <Shield size={14} className="mr-1" />}
                        Change Role
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAppsDialog(u)}
                        data-testid={`app-access-${u.id}`}
                      >
                        <Boxes size={14} className="mr-1" />App Access
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPmRoleDialog(u)}
                        data-testid={`pm-role-${u.id}`}
                        title={u.pm_role ? `Current PM role: ${u.pm_role}` : 'Assign Price Master role'}
                      >
                        <Shield size={14} className="mr-1" />
                        {u.pm_role ? `PM: ${u.pm_role}` : 'PM Role'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isSelf}
                        onClick={() => deleteUser(u)}
                        data-testid={`delete-user-${u.id}`}
                        style={{ borderColor: '#A43E33', color: '#A43E33' }}
                        title={isSelf ? "You can't delete yourself" : ''}
                      >
                        <Trash2 size={14} className="mr-1" />Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Password reset dialog */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent data-testid="password-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{pwUser?.email}</strong>. The existing password cannot be recovered (bcrypt hash); this replaces it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              data-testid="new-password-input"
              type="text"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 6 characters"
            />
            <p className="text-xs text-gray-500">
              Share the new password securely with the user. They should change it at first login.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button onClick={submitPasswordReset} data-testid="confirm-reset-password">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role change dialog */}
      <Dialog open={!!roleUser} onOpenChange={(o) => !o && setRoleUser(null)}>
        <DialogContent data-testid="role-change-dialog">
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
            <DialogDescription>
              Update role for <strong>{roleUser?.email}</strong>. Admins bypass approval workflows and can manage all users and data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="role-select-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleUser(null)}>Cancel</Button>
            <Button onClick={submitRoleChange} data-testid="confirm-change-role">
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Create new user</DialogTitle>
            <DialogDescription>
              Directly register a new user without sending them to the signup page. Share the password securely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="cu-email">Email</Label>
              <Input
                id="cu-email"
                data-testid="create-user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser((s) => ({ ...s, email: e.target.value }))}
                placeholder="jane@company.com"
              />
            </div>
            <div>
              <Label htmlFor="cu-name">Name</Label>
              <Input
                id="cu-name"
                data-testid="create-user-name"
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser((s) => ({ ...s, name: e.target.value }))}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label htmlFor="cu-password">Password</Label>
              <Input
                id="cu-password"
                data-testid="create-user-password"
                type="text"
                autoComplete="new-password"
                value={newUser.password}
                onChange={(e) => setNewUser((s) => ({ ...s, password: e.target.value }))}
                placeholder="Min 6 characters"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser((s) => ({ ...s, role: v }))}>
                <SelectTrigger data-testid="create-user-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={submitCreate} data-testid="confirm-create-user" disabled={creating}>
              {creating ? 'Creating…' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* App Access dialog */}
      <Dialog open={!!appsUser} onOpenChange={(o) => !o && setAppsUser(null)}>
        <DialogContent data-testid="app-access-dialog">
          <DialogHeader>
            <DialogTitle>App Access</DialogTitle>
            <DialogDescription>
              Grant <strong>{appsUser?.email}</strong> access to specific apps under BSC Business OS.
              Admins always have access to all apps regardless of this setting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.budgets}
                onChange={(e) => setApps((a) => ({ ...a, budgets: e.target.checked }))}
                data-testid="app-access-budgets" />
              <span>Budgets (Budget & Finance Tracker)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.sales}
                onChange={(e) => setApps((a) => ({ ...a, sales: e.target.checked }))}
                data-testid="app-access-sales" />
              <span>Sales (Lead & Sales Tracker)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.price_master}
                onChange={(e) => setApps((a) => ({ ...a, price_master: e.target.checked }))}
                data-testid="app-access-price-master" />
              <span>Price Master (Rate Master & GST)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.people}
                onChange={(e) => setApps((a) => ({ ...a, people: e.target.checked }))}
                data-testid="app-access-people" />
              <span>People (Staff & Attendance)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.assets}
                onChange={(e) => setApps((a) => ({ ...a, assets: e.target.checked }))}
                data-testid="app-access-assets" />
              <span>Assets (Asset Tracker)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer"
              style={{ borderColor: '#E8E5DF' }}>
              <input type="checkbox" checked={apps.tickets}
                onChange={(e) => setApps((a) => ({ ...a, tickets: e.target.checked }))}
                data-testid="app-access-tickets" />
              <span>Tickets (Ticket Hub)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppsUser(null)}>Cancel</Button>
            <Button onClick={submitAppAccess} data-testid="app-access-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Master role dialog */}
      <Dialog open={!!pmRoleUser} onOpenChange={(o) => !o && setPmRoleUser(null)}>
        <DialogContent data-testid="pm-role-dialog">
          <DialogHeader>
            <DialogTitle>Price Master role</DialogTitle>
            <DialogDescription>
              Assign the workflow role for <strong>{pmRoleUser?.email}</strong>. Admins bypass role checks.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={pmRole || 'none'} onValueChange={(v) => setPmRole(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="pm-role-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (not in workflow)</SelectItem>
                <SelectItem value="maker">Maker — can create & submit drafts</SelectItem>
                <SelectItem value="checker">Checker — verifies pending_checker</SelectItem>
                <SelectItem value="approver">Approver — finalises pending_approver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPmRoleUser(null)}>Cancel</Button>
            <Button onClick={submitPmRole} data-testid="pm-role-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
